import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { API } from "../../assets/staticData";
import { createSlug } from "../../utils/slug";

export default function EditUser({
  itemToEdit,
  setItemToEdit,
  refresh,
  setRefresh,
}) {
  let token = process.env.REACT_APP_ADMIN_TOKEN;

  const boxRef = useRef(null);
  const [perms, setPerms] = useState([]);
  const [input, setInput] = useState({
    username: "",
    email: "",
    password: "",
    slug: "",
    position: "",
  });
  const [userID, setUserID] = useState();

  const handleClickOutside = (event) => {
    if (boxRef.current && !boxRef.current.contains(event.target)) {
      setItemToEdit({});
      setInput({
        username: "",
        email: "",
        password: "",
        slug: "",
        position: "",
      })
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (Object.keys(itemToEdit).length > 0) {
      boxRef.current.scroll({
        top: 0,
      });
    }
  }, [itemToEdit]);

  useEffect(() => {
    async function fetchData() {
      if (Object.keys(itemToEdit).length > 0) {
        try {
          const response = await fetch(`${API}/users/${itemToEdit.id}`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            method: "GET",
          });
          if (!response.ok) {
            throw new Error("Failed to fetch data");
          }
          const res = await response.json();
          setInput(res);
          setPerms(res.perms);
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      }
    }

    fetchData();
  }, [itemToEdit]);

  const form = new FormData();

  const handleCheckChange = (event, index, type) => {
    const updatedPerms = perms.map((perm, idx) => {
      if (idx === index) {
        return { ...perm, [type]: event.target.checked };
      }
      return perm;
    });
    setPerms(updatedPerms);
  };

  const automateSlug = (title) => {
    const slug = createSlug(title || "");

    setInput({
      ...input,
      slug,
    });
  };

  const editUser = () => {
    if (input.username !== "" && input.email !== "" && input.position !== "" && input.slug !== "") {
      const data = {
        username: input.username,
        email: input.email,
        password: input.password,
        position: input.position,
        slug: input.slug,
        perms: perms,
        certificates: [],
        role: 1,
        confirmed: 1,
      };

      fetch(`${API}/users/${itemToEdit.id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      }).then(async (res) => {
        let response = await res.json();
        if (res.ok) {
          setRefresh(!refresh);
          setItemToEdit({});
          toast("User was updated successfully", {
            position: "top-center",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
            type: "success",
          });
        } else {
          toast(`${response.error?.message}`, {
            position: "top-center",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
            type: "error",
          });
        }
      });
    } else {
      toast("All text fields are required", {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        type: "error",
      });
    }
  };

  return (
    <div
      style={{
        visibility: Object.keys(itemToEdit).length ? "visible" : "hidden",
        opacity: Object.keys(itemToEdit).length ? 1 : 0,
      }}
      className="absolute flex bg-gray-500 bg-opacity-40 w-svw h-svh top-0 left-0 transition-all delay-200"
    >
      <div
        ref={boxRef}
        style={{ justifyContent: "flex-start" }}
        className="bg-white p-8 rounded-md flex-col gap-5 max-h-[600px] overflow-y-scroll"
      >
        <h3>Update User Settings</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2">
          <TextField
            label="Username"
            sx={{ m: 1, width: 350 }}
            value={input.username}
            onChange={(e) => setInput({ ...input, username: e.target.value })}
          />
          <TextField
            label="Email"
            sx={{ m: 1, width: 350 }}
            value={input.email}
            onChange={(e) => setInput({ ...input, email: e.target.value })}
          />
          <TextField
            label="Position"
            sx={{ m: 1, width: 350 }}
            value={input.position}
            onChange={(e) => setInput({ ...input, position: e.target.value })}
          />

          <div className="mt-1 w-1/2  mb-2 ">
            <TextField
              label="Slug"
              sx={{ m: 1, width: 350 }}
              id="slug"
              value={input.slug}
              onChange={(e) => setInput({ ...input, slug: e.target.value })}
            />
            <div className="mt-1 ml-0 cursor-pointer" onClick={() => automateSlug(input.slug)}>
              <p className="text-sm text-right text-sky-700 underline">Automate Slug</p>
            </div>
          </div>
        </div>

        <h3>Permissions</h3>

        <div>
          {perms.map((item, index) => (
            <div
              key={index}
              className="flex"
              style={{
                justifyContent: "space-between",
                minWidth: 800,
                borderBottom: "solid 1px #ddd",
              }}
            >
              <p className="font-semibold">{item.label}</p>
              <FormGroup
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-start",
                  width: "50%",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={item.read}
                      onChange={(event) =>
                        handleCheckChange(event, index, "read")
                      }
                    />
                  }
                  label="Read"
                  style={{ color: "gray" }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={item.edit}
                      onChange={(event) =>
                        handleCheckChange(event, index, "edit")
                      }
                    />
                  }
                  label="Edit"
                  style={{ color: "gray" }}
                />
                {item.hasOwnProperty("add") && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={item.add}
                        onChange={(event) =>
                          handleCheckChange(event, index, "add")
                        }
                      />
                    }
                    label="Add"
                    style={{ color: "gray" }}
                  />
                )}
                {item.hasOwnProperty("delete") && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={item.delete}
                        onChange={(event) =>
                          handleCheckChange(event, index, "delete")
                        }
                      />
                    }
                    label="Delete"
                    style={{ color: "gray" }}
                  />
                )}
                {item.hasOwnProperty("set-password") && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={item["set-password"]}
                        onChange={(event) =>
                          handleCheckChange(event, index, "set-password")
                        }
                      />
                    }
                    label="Set Password"
                    style={{ color: "gray" }}
                  />
                )}
              </FormGroup>
            </div>
          ))}
        </div>

        <div
          onClick={editUser}
          className="bg-blue-500 w-2/5 flex py-3 mt-4 rounded-md text-white hover:bg-blue-600 transition-all delay-100 cursor-pointer"
        >
          Save Changes
        </div>
      </div>
    </div>
  );
}
